/**
 * Cross-platform file save.
 *
 * WEB: the classic object-URL + <a download> click (unchanged behavior).
 * NATIVE (Capacitor): write the file into the app cache via @capacitor/filesystem
 * then open the share sheet via @capacitor/share — the WebView can't trigger a
 * browser "download", so blob/anchor hacks (and the old UA-sniffing path in
 * PuppeteerPdfGenerator) don't work there.
 *
 * @param {Blob|string} data  a Blob, a `data:` URL, or a bare base64 string
 * @param {string} filename   e.g. "report.pdf"
 * @param {string} [mimeType] used when `data` is a bare base64 string
 * @returns {Promise<string|void>} native file URI, or void on web
 */
import { isNative } from './platform';

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      // strip the "data:<mime>;base64," prefix
      const result = String(reader.result || '');
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function toBase64(data) {
  if (data instanceof Blob) return blobToBase64(data);
  if (typeof data === 'string') {
    return data.startsWith('data:') ? data.split(',')[1] : data;
  }
  throw new Error('saveFile: unsupported data type');
}

function webDownload(data, filename, mimeType) {
  let url;
  let revoke = false;
  if (data instanceof Blob) {
    url = URL.createObjectURL(data);
    revoke = true;
  } else if (typeof data === 'string' && data.startsWith('data:')) {
    url = data;
  } else if (typeof data === 'string') {
    url = `data:${mimeType || 'application/octet-stream'};base64,${data}`;
  } else {
    throw new Error('saveFile: unsupported data type');
  }
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  if (revoke) setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function nativeSaveAndShare(data, filename, mimeType) {
  const { Filesystem, Directory } = await import('@capacitor/filesystem');
  const { Share } = await import('@capacitor/share');
  const base64 = await toBase64(data);
  const { uri } = await Filesystem.writeFile({
    path: filename,
    data: base64,
    directory: Directory.Cache,
    recursive: true,
  });
  try {
    await Share.share({ title: filename, url: uri, dialogTitle: filename });
  } catch {
    // user dismissed the share sheet — the file is still written to cache
  }
  return uri;
}

export async function saveFile(data, filename, mimeType) {
  if (!isNative) return webDownload(data, filename, mimeType);
  return nativeSaveAndShare(data, filename, mimeType);
}

export default saveFile;
